import axios from "axios";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

// The website talks to the *mobile app's* backend so a user has one
// account across mobile + web. Auth = JWT in localStorage + `Authorization:
// Bearer <token>` on every request. This matches the mobile app 1:1 and is
// forced by the backend's wildcard CORS (which browsers refuse to combine
// with credentials/cookies).
//
// REACT_APP_BACKEND_URL is a platform-protected variable that always points
// at THIS project's own backend (used for the legacy `/api/contact`
// endpoint) — the mobile backend URL is provided separately via
// REACT_APP_MOBILE_BACKEND_URL so both live-side by side.
const MOBILE_BACKEND = process.env.REACT_APP_MOBILE_BACKEND_URL;
const API = `${MOBILE_BACKEND}/api`;
const TOKEN_KEY = "mp_auth_token";

function readToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
function writeToken(t) {
  try {
    if (t) localStorage.setItem(TOKEN_KEY, t);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

// Axios instance shared with the whole app. Auth header is injected via
// interceptor so a login/logout inside one tab is picked up by every
// component without prop-drilling.
const client = axios.create({ baseURL: API, timeout: 20000 });
client.interceptors.request.use((cfg) => {
  const t = readToken();
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!readToken()) { setUser(null); return null; }
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // 401 = stale token; nuke it so we don't loop.
      if (e?.response?.status === 401) writeToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // Playbook race-fix: if we're returning from Emergent Google Auth with
    // `#session_id=...` on ANY route, skip /auth/me — the root
    // SessionIdHandler in App.js is about to exchange the session_id.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const loginWithPassword = useCallback(async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    writeToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const registerStart = useCallback(async (email, password, name, as_owner = false) => {
    const { data } = await client.post("/auth/register/start", {
      email, password, name, as_owner,
    });
    return data;
  }, []);

  const registerVerify = useCallback(async (email, code) => {
    const { data } = await client.post("/auth/register/verify", { email, code });
    writeToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const registerResend = useCallback(async (email) => {
    const { data } = await client.post("/auth/register/resend", { email });
    return data;
  }, []);

  const forgotPassword = useCallback(async (email) => {
    const { data } = await client.post("/auth/forgot-password", { email });
    return data;
  }, []);

  const resetPassword = useCallback(async (token, new_password) => {
    const { data } = await client.post("/auth/reset-password", { token, new_password });
    return data;
  }, []);

  // Emergent-managed Google sign-in.
  // Playbook: the redirect URL must be a REAL app route (not a bespoke
  // callback route) and the session_id is appended as a URL fragment
  // (`#session_id=...`). A root-level SessionIdHandler in App.js reads
  // that fragment synchronously during render and calls
  // finishGoogleSession, avoiding the AuthProvider/`/auth/me` race.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH.
  const loginWithGoogle = useCallback(() => {
    const redirect = window.location.origin + "/passport";
    window.location.href =
      `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  }, []);

  const finishGoogleSession = useCallback(async (session_id) => {
    const { data } = await client.post("/auth/google-session", { session_id });
    writeToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await client.post("/auth/logout"); } catch {}
    writeToken(null);
    setUser(null);
  }, []);

  // Convenience — role helpers used by the app shell + post-login router.
  const roles = user?.roles || [];
  const currentMode = user?.current_mode || (roles.includes("owner") ? "owner" : "camper");
  const isCamper = roles.includes("camper");
  const isOwner = roles.includes("owner");
  const isAdmin = roles.includes("admin");

  const value = useMemo(() => ({
    user, loading, roles, currentMode, isCamper, isOwner, isAdmin,
    api: client,
    refresh, loginWithPassword, loginWithGoogle, finishGoogleSession,
    registerStart, registerVerify, registerResend,
    forgotPassword, resetPassword, logout,
  }), [
    user, loading, roles, currentMode, isCamper, isOwner, isAdmin,
    refresh, loginWithPassword, loginWithGoogle, finishGoogleSession,
    registerStart, registerVerify, registerResend,
    forgotPassword, resetPassword, logout,
  ]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
